import './BookDetail.css';
import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { fetchBookById, fetchSimplifiedBookById, fetchBooksByAuthor } from '../../services/bookService';
import { fetchAuthorById } from '../../services/authorService';
import { formatAuthorLifespan } from '../../utils/dateUtils';
import { BOOK_TYPES, CHARACTER_ROLES } from '../../constants/bookConstants';

function BookDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [book, setBook] = useState(null);
    const [author, setAuthor] = useState(null);
    const [authorBooks, setAuthorBooks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isPlotExpanded, setIsPlotExpanded] = useState(false);
    const [expandedCharacters, setExpandedCharacters] = useState(new Set());
    const plotSectionRef = useRef(null);

    // Scroll to top and reset expanded states when book ID changes
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setExpandedCharacters(new Set());
        setIsPlotExpanded(false);
    }, [id]);

    useEffect(() => {
        const loadBookData = async () => {
            try {
                setLoading(true);
                setError(null);

                // Fetch both full and simplified data
                const [bookData, simplifiedData] = await Promise.all([
                    fetchBookById(id),
                    fetchSimplifiedBookById(id)
                ]);

                setBook(bookData);

                // Get author info from simplified data
                const authorId = simplifiedData.author?.id;

                if (authorId) {
                    const [authorData, booksData] = await Promise.all([
                        fetchAuthorById(authorId),
                        fetchBooksByAuthor(authorId)
                    ]);

                    setAuthor(authorData);


                    // Filter out the current book - compare as strings to handle type differences
                    const otherBooks = booksData.filter(b => String(b.id) !== String(id));
                    setAuthorBooks(otherBooks);
                }
            } catch (err) {
                console.error('Error loading book:', err);
                setError(err.message || 'Nepodařilo se načíst knihu');
            } finally {
                setLoading(false);
            }
        };

        loadBookData();
    }, [id]);

    if (loading) {
        return (
            <div className="book-detail-page">
                <div className="book-detail-container">
                    <div className="loading-container">
                        <p>Načítání knihy...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !book) {
        return (
            <div className="book-detail-page">
                <div className="book-detail-container">
                    <div className="not-found">
                        <h1>Kniha nenalezena</h1>
                        <p>{error}</p>
                        <button onClick={() => navigate('/knihy')} className="back-button">
                            Zpět na seznam knih
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const getBookTypeLabel = (typeValue) => {
        const bookType = BOOK_TYPES.find(type => type.value === typeValue);
        return bookType ? bookType.label : typeValue;
    };

    const getCharacterRoleLabel = (roleValue) => {
        const role = CHARACTER_ROLES.find(r => r.value === roleValue);
        return role ? role.label : roleValue;
    };

    const handleTogglePlot = () => {
        if (isPlotExpanded && plotSectionRef.current) {
            // Collapsing - scroll to the section
            plotSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        setIsPlotExpanded(!isPlotExpanded);
    };

    const handleToggleCharacter = (index) => {
        setExpandedCharacters(prev => {
            const newSet = new Set(prev);
            if (newSet.has(index)) {
                newSet.delete(index);
            } else {
                newSet.add(index);
            }
            return newSet;
        });
    };

    return (
        <div className="book-detail-page">
            <div className="book-detail-container">
                <button onClick={() => navigate('/knihy')} className="back-button">
                    ← Zpět na knihy
                </button>

                {/* Title Section */}
                <header className="book-header">
                    <h1 className="book-detail-title">{book.bookTitle}</h1>
                    <div className="book-meta">
                        <span className="book-type">{getBookTypeLabel(book.bookType)}</span>
                        {book.genres && book.genres.length > 0 && (
                            <div className="book-genres">
                                {book.genres.map((genre, index) => (
                                    <span key={index} className="genre-tag">{genre}</span>
                                ))}
                            </div>
                        )}
                        {book.publicationYear && (
                            <span className="publication-info">
                                {book.publicationYear}
                            </span>
                        )}
                        {book.countryOfOrigin && (
                            <span className="country-info">
                                {book.countryOfOrigin}
                            </span>
                        )}
                    </div>
                </header>

                {/* Author Section */}
                {author && (
                    <section className="detail-section author-section">
                        <h2 className="section-title">Autor</h2>
                        <div className="author-info">
                            <h3 className="author-name">{author.name}</h3>
                            <p className="author-dates">
                                {formatAuthorLifespan(author.birthDate, author.deathDate, true)}
                            </p>
                            <div className="author-bio">
                                <ReactMarkdown>{author.info}</ReactMarkdown>
                            </div>
                        </div>
                    </section>
                )}

                {/* Summary Section */}
                {book.summary && (
                    <section className="detail-section">
                        <h2 className="section-title">Shrnutí</h2>
                        <div className="section-content">
                            <ReactMarkdown>{book.summary}</ReactMarkdown>
                        </div>
                    </section>
                )}

                {/* Detailed Plot Section */}
                {book.detailedPlot && (
                    <section className="detail-section" ref={plotSectionRef}>
                        <h2 className="section-title">Podrobný děj</h2>
                        <div className="section-content detailed-plot">
                            <div className={`plot-content ${isPlotExpanded ? 'expanded' : 'collapsed'}`}>
                                <ReactMarkdown>
                                    {isPlotExpanded ? book.detailedPlot : book.detailedPlot.substring(0, 400) + (book.detailedPlot.length > 400 ? '...' : '')}
                                </ReactMarkdown>
                            </div>
                            {book.detailedPlot.length > 400 && (
                                <button
                                    className="toggle-plot-button"
                                    onClick={handleTogglePlot}
                                >
                                    {isPlotExpanded ? '▲ Skrýt' : '▼ Zobrazit celý děj'}
                                </button>
                            )}
                        </div>
                    </section>
                )}

                {/* Characters Section */}
                {book.characters && book.characters.length > 0 && (
                    <section className="detail-section">
                        <h2 className="section-title">Postavy</h2>
                        <div className="characters-list">
                            {book.characters.map((character, index) => {
                                const isExpanded = expandedCharacters.has(index);
                                return (
                                    <div key={index} className="character-item" 
                                    onClick={() => character.info && handleToggleCharacter(index)}
                                    style={character.info ? { cursor: 'pointer' } : {}}>
                                        <div className="character-header-row">
                                            <div className="character-header">
                                                <span className="character-name">{character.name}</span>
                                                {character.characterType && (
                                                    <span className="character-role">
                                                        ({getCharacterRoleLabel(character.characterType)})
                                                    </span>
                                                )}
                                            </div>
                                            {character.info && (
                                                <span
                                                    className="toggle-character-button"
                                                    title={isExpanded ? 'Skrýt charakteristiku' : 'Zobrazit charakteristiku'}
                                                >
                                                    {isExpanded ? '▲' : '▼'}
                                                </span>
                                            )}
                                        </div>
                                        {character.info && isExpanded && (
                                            <div className="character-description">
                                                <ReactMarkdown>{character.info}</ReactMarkdown>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                )}

                {/* Historical Era Section */}
                {book.historyEra && (
                    <section className="detail-section">
                        <h2 className="section-title">Historická éra</h2>
                        <div className="section-content">
                            <ReactMarkdown>{book.historyEra}</ReactMarkdown>
                        </div>
                    </section>
                )}

                {/* Cultural Context Section */}
                {book.culturalContext && (
                    <section className="detail-section">
                        <h2 className="section-title">Kulturní kontext</h2>
                        <div className="section-content">
                            <ReactMarkdown>{book.culturalContext}</ReactMarkdown>
                        </div>
                    </section>
                )}

                {/* Recognition Section */}
                {book.recognition && (
                    <section className="detail-section">
                        <h2 className="section-title">Uznání a význam</h2>
                        <div className="section-content">
                            <ReactMarkdown>{book.recognition}</ReactMarkdown>
                        </div>
                    </section>
                )}

                {/* Other Info Section */}
                {book.otherInfo && (
                    <section className="detail-section">
                        <h2 className="section-title">Další informace</h2>
                        <div className="section-content">
                            <ReactMarkdown>{book.otherInfo}</ReactMarkdown>
                        </div>
                    </section>
                )}

                {/* Other Books by Author Section */}
                {authorBooks.length > 0 && (
                    <section className="detail-section">
                        <h2 className="section-title">Další knihy autora</h2>
                        <div className="author-books-list">
                            {authorBooks.map((authorBook) => (
                                <div
                                    key={authorBook.id}
                                    className="author-book-item"
                                    onClick={() => navigate(`/knihy/${authorBook.id}`)}
                                >
                                    <h3 className="author-book-title">{authorBook.bookTitle}</h3>
                                    <span className="author-book-arrow">→</span>
                                </div>
                            ))}
                        </div>
                    </section>
                )}
            </div>
        </div>
    );
}

export default BookDetail;
